/**
 * The official-results source registry (research and quotes: docs/source/research/official-results.md, fetched 2026-10-04).
 * One entry per department dataset or lookup. `sync` sources are fetched by the monthly job (services/api/src/official/);
 * `link_only` ones are only linked to, with the reason. Each entry carries:
 *  - where to fetch, the format, the schema fingerprint (expected columns and types), and the parser;
 *  - the basis we show facts under (owner, 2026-10-04: "We could always link to the source. Quoting is not a sin."): facts
 *    (scores, grades, dates, violations) are shown with "Source: <department>" and a link; the LIMIT is the collection method;
 *  - `flag`: terms or robots text that restricts automated access, quoted with its URL, for the owner to decide (a flag, not an
 *    automatic link-only; robots disallows, rate limits and blocks are hard stops and never evaded);
 *  - the department's own lookup, and how to find this place there.
 */
import { byNewest, isoDay, publicPhone, tidyName, VIOLATION_TEXT_MAX, type FingerprintField, type OfficialCategory, type OfficialInspection, type OfficialPlace, type OfficialPlaceRecord, type OfficialScale, type OfficialViolation } from "./official.ts";
import type { UsState } from "./states/types.ts";
import { packFor, STATE_INDEX } from "./states/index.ts";
import { isSupportedState } from "./states/types.ts";
import { directoryData } from "./directory.ts";
import { rulesWorld, SHAPES, valueOf } from "./ruleshapes.ts";
import { SLCO_SOURCES, SLCO_SCALE } from "./states/ut/official.ts";
import { LA_SOURCES, LA_SCALE, laCategory } from "./official-la.ts";
import { PAS_SOURCES, PAS_SCALE } from "./official-pasadena.ts";
import { SCC_SOURCES, SCC_SCALE } from "./official-sccgov.ts";
import { MARIN_SOURCES, MARIN_SCALE, makeMarin } from "./official-marin.ts";
import type { CodeField } from "./official-records.ts";
export { slcoCategory } from "./states/ut/official.ts";
import { UT_READABLE_COPIES } from "./states/ut/official.ts";
/** A readable copy of a rule page that shows its text only in a browser (the source re-check, services/api/src/sourcecheck/job.ts). */
export type ReadableCopy = { url: string; freshDays: number };
/** Each state pack's readable copies, keyed by state (one place, one truth: never one state's list read as everyone's). */
const READABLE_COPIES_BY_STATE: Partial<Record<UsState, Readonly<Record<string, ReadableCopy>>>> = { UT: UT_READABLE_COPIES };
/** The readable copy for a quoted address (any of its spellings), from whichever state pack lists that address; undefined when none does. */
export function readableCopyOf(...urls: string[]): ReadableCopy | undefined {
  for (const copies of Object.values(READABLE_COPIES_BY_STATE)) for (const u of urls) if (copies?.[u]) return copies[u];
  return undefined;
}
export { laCategory } from "./official-la.ts";
export * from "./official-pasadena.ts";
export * from "./official-sccgov.ts";
export * from "./official-marin.ts";
export * from "./official-fl.ts";
import { FL_DBPR_ITEMS, FL_MARK_WORDS } from "./official-fl.ts";
import { ITEM_MARKS, ITEM_ORDER } from "./official.ts";
// Florida's marks and order beside each item: DBPR's dagger (risk factor) and asterisk (primary concern), in its key's words.
ITEM_MARKS["fl-dbpr"] = (code) => { const f = FL_DBPR_ITEMS[code]; return f ? [...(f.riskFactor ? [FL_MARK_WORDS.riskFactor] : []), ...(f.primaryConcern ? [FL_MARK_WORDS.primaryConcern] : [])] : undefined; };
ITEM_ORDER["fl-dbpr"] = (code) => { const f = FL_DBPR_ITEMS[code]; return (f?.riskFactor ? 0 : f?.primaryConcern ? 100 : 200) + Number(code); };
import { SF_SOURCES, SF_SCALE, makeSfParser } from "./official-sf.ts";
export * from "./official-sf.ts";

export type SourceBasis = { kind: "facts"; terms: string; quote: string; url: string } | { kind: "link_only"; reason: string };

/**
 * How to read one CDP EHS public portal (a CDP, Inc. ASP.NET WebForms site, "public.cdpehs.com/<Site>/"). The labels are the
 * schema fingerprint: the page's own words for each column. Any missing label = the portal changed shape = down.
 */
export interface CdpConfig {
  /** The page the department's own link opens (the portal redirects it to a cookieless-session URL). */
  entryUrl: string;
  /** Inspection areas (the portal's "Inspection Area" filter) we read: the rest (pools, tattoo, massage...) are not food. */
  areas: string[];
  /** Expected labels per page type. */
  labels: { list: string[]; history: string[]; report: string[]; closures: string[] };
  /** Our floor between requests; the code never goes under 3000. */
  minGapMs: number;
  /** The nightly window in the department's time zone: the job starts only inside it and stops at its end. */
  window: { tz: string; startHour: number; endHour: number };
}

export interface OfficialSource {
  id: string;
  state: UsState;
  /** The jurisdiction node id (core jurisdiction.ts) whose scoring system the numbers are on. */
  jurisdiction: string;
  /** The department, as credited on every result ("Source: …"). */
  department: string;
  /** The place name a diner uses ("New York City"); for a statewide source, each place's county is its area. */
  area: string;
  status: "sync" | "link_only";
  /** socrata_csv: a SODA CSV query paged by $offset; csv_files: whole files, each with its own conditional GET; cdp_ehs: CDP's public EHS portal (services/api/src/official/cdp.ts), walked page by page at one request per 3 seconds. */
  format?: "socrata_csv" | "csv_files" | "cdp_ehs" | "pasadena_api" | "sccgov_lives";
  /** cdp_ehs only. */
  cdp?: CdpConfig;
  /** pasadena_api only: its pace and nightly window (the same window shape as CDP). */
  night?: { minGapMs: number; window: CdpConfig["window"] };
  /**
   * false = collected in full but NOT shown to diners (the API and the pages serve the link-only state) until the owner reviews a
   * sample and flips it (owner, 2026-10-05). Absent = shown. Staff see the counts and a sample (services/api/src/official/service.ts staffView).
   */
  displayApproved?: boolean;
  urls?: string[];
  /** csv_files only: files of different shapes (each its own role, fingerprint and columns); `urls` lists the same urls in the same order. */
  files?: { url: string; role: string; fingerprint: FingerprintField[]; keepColumns?: string[]; lazyColumns?: string[] }[];
  /** csv_files only: ask for one byte of each file first and compare its Last-Modified with the last good fetch (for hosts that ignore conditional GETs). */
  probe?: "range";
  /** Least time between our requests to this source's hosts (the sync's own pause is the larger of this and its default). */
  minGapMs?: number;
  /** Socrata only: the query (the job adds $where on the date window, $order, $limit, $offset). */
  socrata?: { select: string; dateField: string; order: string };
  pageSize?: number;
  /** socrata_csv: the host ignores its weak ETag, so a recheck sends only If-Modified-Since (Marin; the sync's `cond`). */
  conditional?: "last_modified";
  /** How many years of inspections to fetch (the department's own window is often 3). */
  years?: number;
  fingerprint?: FingerprintField[];
  /** The parser id (PARSERS below). */
  parser?: string;
  /** Columns the parser reads beyond the fingerprint's (the sync keeps only these and the fingerprint's: memory). */
  keepColumns?: string[];
  /** Long text columns decoded only when the parser reads them (memory). */
  lazyColumns?: string[];
  /** Whether the source names a kind of place we can map; false = typical ranges cover all food places together. */
  categories?: boolean;
  updates: string;
  basis: SourceBasis;
  /** Restrictive terms or robots text for the owner (<= 15 words quoted) and where it is. */
  flag?: { clause: string; url: string };
  /** About how many places the source holds when fully read (a night-only crawl shows "N of ~M places" while it loads). */
  expectedPlaces?: number;
  attribution: string;
  /** The department's own search page (always shown). */
  lookupUrl: string;
  /** How to find this place on that page ("Search for the name or license 2300027"). */
  recordHint: string;
  /** The department's own scale in plain words when the state pack's shape cannot say it (cited); used on the area page. */
  scaleLine?: { line: string; citation: string; url: string };
  /** How often inspections happen there, from the packs or the department (shown on the jurisdiction page). */
  howOften?: { text: string; url: string };
  /** Months between routine inspections per schedule level, from the department's published schedule (Salt Lake: level 1 once a year … level 4 three times a year). Used only when a place's record gives its level. */
  levelMonths?: Readonly<Record<number, number>>;
}

const UA_NOTE = "CleanCleaver/1.0 (official-results sync; +https://cleancleaver.com)";
export const OFFICIAL_USER_AGENT = UA_NOTE;

// ── Scales (from the state packs' inspection.scoring shapes; never invented here) ───────────────────────────────────

export const SCALES: Record<string, OfficialScale> = {
  "NY/nyc:points": {
    id: "NY/nyc:points", kind: "points_lower_better", unit: "points", noun: "point violations", lowerIsBetter: true, min: 0, max: 42,
    bands: [{ grade: "A", min: 0, max: 13 }, { grade: "B", min: 14, max: 27 }, { grade: "C", min: 28, max: 999 }],
    meaning: "Inspectors add points for each problem they find. 0 to 13 points is an A, 14 to 27 a B, 28 or more a C.",
    source: { citation: "24 RCNY Chapter 23; NYC Health, restaurant grades", url: "https://www.nyc.gov/site/doh/services/restaurant-grades.page" },
  },
  "TX/austin-travis:score": {
    id: "TX/austin-travis:score", kind: "score_higher_better", unit: "", noun: "points taken off", lowerIsBetter: false, min: 50, max: 100,
    meaning: "Each inspection starts at 100 and points come off for each problem found. The data labels 90 and up green and 70 to 89 yellow.",
    source: { citation: "Austin Public Health, Food Establishment Inspection Scores (data.austintexas.gov, ecmv-9xxi), column inspectionscorecategory", url: "https://data.austintexas.gov/d/ecmv-9xxi" },
  },
  "FL:violations": {
    id: "FL:violations", kind: "violations_lower_better", unit: "violations", noun: "violations", lowerIsBetter: true, min: 0, max: 20,
    meaning: "Florida gives no score or grade. Each inspection lists the violations found, sorted into high priority, intermediate and basic.",
    source: { citation: "Fla. Stat. 509.039 (ranking preempted to the state); DBPR inspection data files", url: "https://www2.myfloridalicense.com/hotels-restaurants/public-records/" },
  },
};
SCALES[SLCO_SCALE.id] = SLCO_SCALE;
SCALES[LA_SCALE.id] = LA_SCALE;
SCALES[PAS_SCALE.id] = PAS_SCALE;
SCALES[SCC_SCALE.id] = SCC_SCALE;
SCALES[MARIN_SCALE.id] = MARIN_SCALE;
SCALES[SF_SCALE.id] = SF_SCALE;
export const scaleFor = (jurisdiction: string): OfficialScale | undefined => Object.values(SCALES).find((s) => s.id.startsWith(`${jurisdiction}:`));

// ── The registry ───────────────────────────────────────────────────────────────────────────────────────────────────

const NYC_FIELDS = "camis,dba,boro,building,street,zipcode,phone,cuisine_description,inspection_date,action,violation_code,violation_description,critical_flag,score,grade,inspection_type,latitude,longitude";
/** Florida's 58 numbered violation columns ("Violation 01" to "Violation 58"): each holds how many times that item was cited on the visit. */
export const FL_ITEM_COLUMNS: readonly string[] = Array.from({ length: 58 }, (_, i) => `violation ${String(i + 1).padStart(2, "0")}`);
const FL_FILES = [1, 2, 3, 4, 5, 6, 7].map((d) => `https://www2.myfloridalicense.com/sto/file_download/extracts/${d}fdinspi.csv`);

export const OFFICIAL_SOURCES: readonly OfficialSource[] = [
  {
    id: "nyc-dohmh", state: "NY", jurisdiction: "NY/nyc", department: "New York City Department of Health and Mental Hygiene", area: "New York City", status: "sync",
    format: "socrata_csv", urls: ["https://data.cityofnewyork.us/resource/43nn-pn8j.csv"],
    socrata: { select: NYC_FIELDS, dateField: "inspection_date", order: "camis,inspection_date,violation_code" }, pageSize: 10_000, years: 3,
    fingerprint: [
      { name: "camis", type: "text", required: true }, { name: "dba", type: "text" }, { name: "boro", type: "text" }, { name: "building", type: "text" },
      { name: "street", type: "text" }, { name: "zipcode", type: "text" }, { name: "phone", type: "text" }, { name: "cuisine_description", type: "text" },
      { name: "inspection_date", type: "date", required: true }, { name: "violation_code", type: "text" }, { name: "violation_description", type: "text" },
      { name: "critical_flag", type: "text" }, { name: "score", type: "number" }, { name: "grade", type: "text" }, { name: "inspection_type", type: "text" },
      { name: "latitude", type: "number" }, { name: "longitude", type: "number" },
    ],
    parser: "nyc", lazyColumns: ["violation_description"], categories: true, updates: "daily (we check monthly)",
    basis: { kind: "facts", terms: "NYC OpenData, attribution to DOHMH", quote: "Department of Health and Mental Hygiene (DOHMH)", url: "https://data.cityofnewyork.us/d/43nn-pn8j" },
    attribution: "NYC Department of Health and Mental Hygiene, via NYC OpenData",
    lookupUrl: "https://a816-health.nyc.gov/ABCEatsRestaurants/#/Search",
    recordHint: "Search for the name on NYC Health's restaurant grades lookup.",
    howOften: { text: "NYC inspects each restaurant at least once a year. A place scoring 14 or more on an initial inspection gets a second, graded inspection.", url: "https://www.nyc.gov/site/doh/services/restaurant-grades.page" },
  },
  {
    id: "austin-aph", state: "TX", jurisdiction: "TX/austin-travis", department: "Austin Public Health", area: "Austin and Travis County", status: "sync",
    format: "socrata_csv", urls: ["https://data.austintexas.gov/resource/ecmv-9xxi.csv"],
    socrata: { select: "restaurant_name,zip_code,inspection_date,score,address,facility_id,process_description,lat,lng,inspectionid", dateField: "inspection_date", order: "facility_id,inspection_date" }, pageSize: 10_000, years: 3,
    fingerprint: [
      { name: "restaurant_name", type: "text", required: true }, { name: "zip_code", type: "text" }, { name: "inspection_date", type: "date", required: true },
      { name: "score", type: "number", required: true }, { name: "address", type: "text" }, { name: "facility_id", type: "text", required: true },
      { name: "process_description", type: "text" }, { name: "lat", type: "number" }, { name: "lng", type: "number" }, { name: "inspectionid", type: "text" },
    ],
    parser: "austin", categories: false, updates: "as inspections are entered (we check monthly)",
    basis: { kind: "facts", terms: "Public Domain (City of Austin open data)", quote: "Public Domain", url: "https://data.austintexas.gov/api/views/ecmv-9xxi.json" },
    attribution: "City of Austin, Texas, data.austintexas.gov",
    lookupUrl: "https://inspections.myhealthdepartment.com/aph",
    recordHint: "Search for the name on Austin Public Health's inspection lookup.",
  },
  {
    id: "fl-dbpr", state: "FL", jurisdiction: "FL", department: "Florida Department of Business and Professional Regulation, Division of Hotels and Restaurants", area: "Florida", status: "sync",
    format: "csv_files", urls: FL_FILES,
    fingerprint: [
      { name: "county name", type: "text", required: true }, { name: "license type code", type: "text" }, { name: "license number", type: "text", required: true },
      { name: "business (dba-does business as) name", type: "text", required: true }, { name: "location address", type: "text" }, { name: "location city", type: "text" },
      { name: "location zip code", type: "text" }, { name: "visit number", type: "text" }, { name: "inspection type", type: "text" }, { name: "inspection disposition", type: "text" },
      { name: "inspection date", type: "date", required: true }, { name: "number of total violations", type: "number" }, { name: "number of high priority violations", type: "number" },
      { name: "number of intermediate violations", type: "number" }, { name: "number of basic violations", type: "number" }, { name: "license id", type: "text" }, { name: "inspection visit id", type: "text" },
    ],
    parser: "fl", keepColumns: ["inspection number", ...FL_ITEM_COLUMNS], categories: false, updates: "weekly (we check monthly)",
    basis: { kind: "facts", terms: "Florida public records (Chapter 119), free download", quote: "the DBPR provides copies of electronic records to the public through free download", url: "https://www2.myfloridalicense.com/public-records-read-medisclaimer/" },
    attribution: "Florida DBPR, Division of Hotels and Restaurants, inspection data files",
    lookupUrl: "https://www.myfloridalicense.com/portalsearches/VerifyLicensee?Mode=0&BoardType=H",
    recordHint: "Search DBPR's license lookup for the license number.",
    howOften: { text: "Florida's Division of Hotels and Restaurants inspects licensed food places on its own schedule; the data file lists each visit this fiscal year.", url: "https://www2.myfloridalicense.com/hotels-restaurants/public-records/" },
  },
  ...SLCO_SOURCES,
  ...LA_SOURCES,
  ...PAS_SOURCES,
  ...SCC_SOURCES,
  ...MARIN_SOURCES,
  ...SF_SOURCES,
  // ── Link only (not added yet, or flagged for the owner) ──
  linkOnly("snhd", "NV", "NV/snhd", "Southern Nevada Health District", "Clark County", "https://www.southernnevadahealthdistrict.org/permits-and-regulations/restaurant-inspections/restaurant-inspection-search/",
    "Link only for now: a nightly bulk file exists, but the district's policy page limits use of its data sheets to informational or personal use with no modifications; the owner asks SNHD first.",
    { clause: "use of such documents … is for informational or personal use only. No modifications", url: "https://www.southernnevadahealthdistrict.org/about-us/general-information/policies-regulations/" }),
  linkOnly("boston-isd", "MA", "MA/boston", "City of Boston Inspectional Services Department", "Boston", "https://www.boston.gov/departments/inspectional-services/mayors-food-court",
    "Not added yet: open data (PDDL), but only pass/fail and 904,260 rows; robots disallows /api/, so the bulk CSV would be used.",
    { clause: "robots.txt: Disallow: /api/", url: "https://data.boston.gov/robots.txt" }),
  linkOnly("nys-doh", "NY", "NY", "New York State Department of Health", "New York State", "https://health.data.ny.gov/",
    "Flagged for the owner: the state's notes say the data is not for grading restaurants.",
    { clause: "Any use of this data to develop a restaurant grading system is not supported", url: "https://health.data.ny.gov/d/cnih-y5dw" }),
  // Names from the pack's node (state words live in packages/core/src/states/: scripts/state-coupling.test.ts).
  linkOnly("utah-county", "UT", "UT/utah-county", "", "", "https://inspections.myhealthdepartment.com/utah-utahcounty",
    "No machine-readable data found; the lookup blocks automated requests (HTTP 403), so we never fetch it.",
    { clause: "HTTP 403 to automated requests (a technical block)", url: "https://inspections.myhealthdepartment.com/utah-utahcounty" }),
  linkOnly("nnph", "NV", "NV/nnph", "Northern Nevada Public Health", "Washoe County", "https://www.nnph.org/resources/food-inspections.php", "No machine-readable data found (an app only)."),
  linkOnly("oha", "OR", "OR", "Oregon Health Authority", "Oregon", "https://inspections.myhealthdepartment.com/oregon",
    "No machine-readable data found; the lookup blocks automated requests (HTTP 403).",
    { clause: "HTTP 403 to automated requests (a technical block)", url: "https://inspections.myhealthdepartment.com/oregon" }),
];

function linkOnly(id: string, state: UsState, jurisdiction: string, department: string, area: string, lookupUrl: string, reason: string, flag?: { clause: string; url: string }, scaleLine?: OfficialSource["scaleLine"]): OfficialSource {
  return { id, state, jurisdiction, department, area, status: "link_only", updates: "", basis: { kind: "link_only", reason }, ...(flag ? { flag } : {}), ...(scaleLine ? { scaleLine } : {}), attribution: department, lookupUrl, recordHint: "Search for the name on the department's lookup." };
}

/** A source read slowly at night, one request at a time (CDP portals and Pasadena's JSON portal). */
export const isNightSource = (s: Pick<OfficialSource, "format">) => s.format === "cdp_ehs" || s.format === "pasadena_api";
/** Its nightly window. */
export const nightWindowOf = (s: Pick<OfficialSource, "cdp" | "night">): CdpConfig["window"] => (s.cdp?.window ?? s.night!.window);
export const sourceById = (id: string) => { const s = OFFICIAL_SOURCES.find((x) => x.id === id); return s ? named(s) : undefined; };
export const sourceForJurisdiction = (j: string) => { const s = OFFICIAL_SOURCES.find((x) => x.jurisdiction === j); return s ? named(s) : undefined; };
/** Whether a source's results may be shown to diners: the owner's run-time choice (the Data dashboard) wins over the registry's. */
export const displayApprovedOf = (s: Pick<OfficialSource, "displayApproved">, override?: boolean | null): boolean => override ?? s.displayApproved ?? true;
export const SYNC_SOURCES = (): OfficialSource[] => OFFICIAL_SOURCES.filter((s) => s.status === "sync");

/** The first line of the source's own words for why we show its facts, or why we only link. */
export const basisLine = (s: OfficialSource) => (s.basis.kind === "facts" ? `Facts shown under ${s.basis.terms}.` : `Link only: ${s.basis.reason}`);

// ── Categories: the source's own words → ours (only what the words actually say) ──────────────────────────────────

const NYC_COFFEE = new Set(["coffee/tea"]);
const NYC_COUNTER = new Set(["hamburgers", "hotdogs", "hotdogs/pretzels", "chicken", "sandwiches", "sandwiches/salads/mixed buffet", "donuts", "bagels/pretzels", "juice, smoothies, fruit salads", "frozen desserts", "pizza", "soups/salads/sandwiches"]);
export function nycCategory(cuisine: string): OfficialCategory {
  const c = cuisine.trim().toLowerCase();
  if (NYC_COFFEE.has(c)) return "coffee";
  if (NYC_COUNTER.has(c)) return "fast_food";
  return "other";
}

// ── Parsers: rows (one per violation, or one per inspection) → places with every inspection and every violation ───────

/** Which field of a violation is the code's own text, kept once per jurisdiction in the code book (official-records.ts). */
// A CDP portal and Santa Clara County's LIVES feed give the code's title as `rationale` and the inspector's own line as `text`.
// Marin gives no title for a code, only the inspector's line for that visit: nothing goes to the book, every line stays whole on its violation.
export const codeFieldsFor = (sourceId: string): CodeField[] => { const f = sourceById(sourceId)?.format; return sourceId === "marin" ? [] : f === "cdp_ehs" || f === "sccgov_lives" || sourceId === "sf-dph" ? ["rationale"] : ["text"]; };

type Row = Record<string, string>;
interface Draft { place: OfficialPlace; inspections: Map<string, OfficialInspection> }

/** A source's rows came in a different order from the one we asked for (a place seen again after it was finished). */
export class RowsOutOfOrder extends Error { constructor(public key: string) { super(`the rows are no longer in the order we asked for (${key} came back after it was finished)`); } }

const recordOf = (d: Draft, sourceId: string, fetchedAt: string, hash: string): OfficialPlaceRecord | null => {
  const inspections = [...d.inspections.values()].sort(byNewest);
  return inspections.length ? { place: d.place, inspections, provenance: { sourceId, fetchedAt, hash } } : null;
};
function finish(drafts: Map<string, Draft>, sourceId: string, fetchedAt: string, hash: string): OfficialPlaceRecord[] {
  const out: OfficialPlaceRecord[] = [];
  for (const d of drafts.values()) { const r = recordOf(d, sourceId, fetchedAt, hash); if (r) out.push(r); }
  drafts.clear();
  return out.sort((a, b) => (a.place.key < b.place.key ? -1 : 1));
}

/**
 * For a source whose rows come sorted by place (Socrata `$order` starts with the place id): one place is open at a time; when the
 * next place starts, the open one is finished and waits in `ready` until the sync drains it to the store (memory: NYC's three years
 * never sit in memory at once). A place seen again after it was finished means the order broke: RowsOutOfOrder, nothing is stored.
 */
function ordered(sourceId: string, fetchedAt: string, hash: string) {
  let open: Draft | null = null;
  const ready: OfficialPlaceRecord[] = [];
  const finished = new Set<string>();
  const close = () => { if (!open) return; const r = recordOf(open, sourceId, fetchedAt, hash); if (r) ready.push(r); finished.add(open.place.key); open = null; };
  return {
    /** The draft for `key`, opening it with `make` when it is a new place. */
    at(key: string, make: () => Draft): Draft {
      if (open?.place.key === key) return open;
      if (finished.has(key)) throw new RowsOutOfOrder(key);
      close();
      open = make();
      return open;
    },
    drain: () => ready.splice(0),
    done: () => { close(); return ready.splice(0).sort((a, b) => (a.place.key < b.place.key ? -1 : 1)); },
    /** Places held in memory right now (the open one plus the finished ones not drained yet). */
    held: () => ready.length + (open ? 1 : 0),
  };
}
const num = (v: string | undefined): number | null => (v === undefined || v.trim() === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const geoOf = (lat?: string, lon?: string) => { const a = num(lat), b = num(lon); return a !== null && b !== null && a !== 0 && b !== 0 ? { geo: { lat: a, lon: b } } : {}; };
/** One copy of each distinct string (a code's text repeats on thousands of rows; memory). */
const interner = () => { const m = new Map<string, string>(); return (s: string) => { const h = m.get(s); if (h !== undefined) return h; m.set(s, s); return s; }; };

/** NYC: one row per violation; an inspection is (camis, date, type). Score and grade repeat on each row. 1900-01-01 = not inspected yet. */
function makeNyc(fetchedAt: string, hash: string): OfficialParser {
  const src = sourceById("nyc-dohmh")!;
  const o = ordered(src.id, fetchedAt, hash);
  const keep = interner();
  const add = (rows: readonly Row[]) => { for (const r of rows) {
    const id = r.camis ?? "", date = isoDay(r.inspection_date ?? "");
    if (!id || !date || date < "1901-01-01") continue;
    const key = `${src.id}:${id}`;
    const d = o.at(key, () => ({ place: { key, sourceId: src.id, jurisdiction: src.jurisdiction, area: src.area, extId: id, name: tidyName(r.dba ?? ""), address: tidyName(`${r.building ?? ""} ${r.street ?? ""}`.trim()), city: r.boro ?? "", zip: r.zipcode ?? "", ...(publicPhone(r.phone ?? "") ? { phone: publicPhone(r.phone ?? "") } : {}), ...geoOf(r.latitude, r.longitude), ...(num(r.latitude) ? { geoSource: "source" as const } : {}), sourceCategory: r.cuisine_description ?? "", category: nycCategory(r.cuisine_description ?? ""), recordUrl: src.lookupUrl, recordHint: src.recordHint, department: src.department, dataUrl: `https://data.cityofnewyork.us/resource/43nn-pn8j.json?camis=${encodeURIComponent(id)}` }, inspections: new Map() }));
    const type = r.inspection_type ?? "";
    const ik = `${date}|${type}`;
    let i = d.inspections.get(ik);
    if (!i) {
      i = { placeKey: key, date, score: num(r.score), grade: r.grade || null, result: null, scaleId: "NY/nyc:points", type: type ? keep(type) : null, routine: /initial inspection/i.test(type) && /cycle/i.test(type), violations: { count: 0, critical: 0, items: [] }, recordUrl: src.lookupUrl };
      d.inspections.set(ik, i);
    }
    if (r.violation_code) {
      const critical = /^critical$/i.test(r.critical_flag ?? "");
      i.violations.count++; if (critical) i.violations.critical++;
      i.violations.items.push({ code: keep(r.violation_code), text: keep((r.violation_description ?? "").slice(0, VIOLATION_TEXT_MAX)), critical });
    }
  }
  };
  return { add, drain: o.drain, held: o.held, done: o.done };
}

/** Austin: one row per inspection, with a score out of 100 (the data lists no violations). No kind-of-place column, so no categories. */
function makeAustin(fetchedAt: string, hash: string): OfficialParser {
  const src = sourceById("austin-aph")!;
  const o = ordered(src.id, fetchedAt, hash);
  const keep = interner();
  const add = (rows: readonly Row[]) => { for (const r of rows) {
    const id = r.facility_id ?? "", date = isoDay(r.inspection_date ?? "");
    if (!id || !date) continue;
    const key = `${src.id}:${id}`;
    // "320 S Capital of Texas Hwy Ste B West Lake Hills, TX 78746-0032": the city is the words before ", TX".
    const d = o.at(key, () => ({ place: { key, sourceId: src.id, jurisdiction: src.jurisdiction, area: src.area, extId: id, name: tidyName(r.restaurant_name ?? ""), address: (r.address ?? "").replace(/,\s*TX\s+[\d-]+$/i, ""), city: "", zip: (r.zip_code ?? "").slice(0, 5), ...geoOf(r.lat, r.lng), ...(num(r.lat) ? { geoSource: "source" as const } : {}), sourceCategory: "", category: "other", recordUrl: src.lookupUrl, recordHint: src.recordHint, department: src.department, dataUrl: `https://data.austintexas.gov/resource/ecmv-9xxi.json?facility_id=${encodeURIComponent(id)}` }, inspections: new Map() }));
    const ik = r.inspectionid || `${date}|${r.process_description ?? ""}`;
    if (d.inspections.has(ik)) continue;
    const type = r.process_description ?? "";
    d.inspections.set(ik, { placeKey: key, date, score: num(r.score), grade: null, result: null, scaleId: "TX/austin-travis:score", type: type ? keep(type) : null, routine: /routine/i.test(type), violations: { count: 0, critical: 0, items: [] }, recordUrl: src.lookupUrl });
  }
  };
  return { add, drain: o.drain, held: o.held, done: o.done };
}

/** DBPR's county names → ours ("Dade" is Miami-Dade). */
const FL_COUNTY_FIX: Record<string, string> = { dade: "Miami-Dade", "st. johns": "St. Johns", "st. lucie": "St. Lucie", desoto: "DeSoto" };
export const flCountyName = (raw: string) => FL_COUNTY_FIX[raw.trim().toLowerCase()] ?? tidyName(raw);

/**
 * Florida: one row per inspection visit, counts of violations by tier (no score, no grade) and a count per numbered violation
 * column ("Violation 01" to "Violation 58"; their text is DBPR's published key, and an item's count never says its tier). The license
 * number is how a diner finds the place on DBPR's lookup; the per-visit page we tried (inspectionDetail.asp) answers 200 with an
 * error page, so we link to the lookup instead. License types are not split into our categories (their meanings are not in the file).
 */
function makeFl(fetchedAt: string, hash: string): OfficialParser {
  const src = sourceById("fl-dbpr")!;
  const drafts = new Map<string, Draft>();
  const keep = interner();
  const itemCodes = FL_ITEM_COLUMNS.map((c) => c.slice("violation ".length));
  const add = (rows: readonly Row[]) => { for (const r of rows) {
    const lic = r["license number"] ?? "", date = isoDay(r["inspection date"] ?? "");
    if (!lic || !date) continue;
    const key = `${src.id}:${lic}`;
    const county = flCountyName(r["county name"] ?? "");
    let d = drafts.get(key);
    if (!d) {
      d = { place: { key, sourceId: src.id, jurisdiction: src.jurisdiction, area: `${county} County`, extId: lic, name: tidyName(r["business (dba-does business as) name"] ?? ""), address: tidyName(r["location address"] ?? ""), city: tidyName(r["location city"] ?? ""), zip: (r["location zip code"] ?? "").slice(0, 5), sourceCategory: `License type ${r["license type code"] ?? ""}`.trim(), category: "other", recordUrl: src.lookupUrl, recordHint: `Search DBPR's license lookup for license number ${lic}.`, department: src.department }, inspections: new Map() };
      drafts.set(key, d);
    }
    const ik = r["inspection visit id"] || `${date}|${r["inspection number"] ?? ""}`;
    if (d.inspections.has(ik)) continue;
    const hp = num(r["number of high priority violations"]) ?? 0, mid = num(r["number of intermediate violations"]) ?? 0, basic = num(r["number of basic violations"]) ?? 0;
    const total = num(r["number of total violations"]) ?? hp + mid + basic;
    const type = r["inspection type"] ?? "";
    const items: OfficialViolation[] = [];
    // DBPR's own text for each numbered item (its key, official-fl.ts): kept once in the code book, not per row.
    FL_ITEM_COLUMNS.forEach((c, k) => { const n = num(r[c]); if (n && n > 0) items.push({ code: itemCodes[k]!, text: FL_DBPR_ITEMS[itemCodes[k]!]?.text ?? "", critical: false, occurrences: n }); });
    d.inspections.set(ik, {
      placeKey: key, date, score: total, grade: null, result: r["inspection disposition"] ? keep(r["inspection disposition"]) : null, scaleId: "FL:violations", type: type ? keep(type) : null,
      // A call-back is a second visit to check a fix (visit number 2 and up): never routine.
      routine: /^routine/i.test(type) && (r["visit number"] ?? "1").trim() === "1",
      violations: { count: total, critical: hp, items, tiers: { high: hp, intermediate: mid, basic } }, recordUrl: src.lookupUrl,
    });
  }
  };
  return { add, done: () => finish(drafts, src.id, fetchedAt, hash) };
}


/**
 * Los Angeles County: three files in one run, in this order. INSPECTIONS (one row per inspection, per health program inside a
 * facility: the program's RECORD ID is the place, since two programs at one address have different names) → VIOLATIONS (one row
 * per violation, joined by SERIAL NUMBER) → INVENTORY (coordinates by RECORD ID). Programs the county lists as INACTIVE are
 * dropped. A violation worth 2 points or more is a critical risk factor (the county's guide, p. 11: critical risk factors cost 4
 * or 2 points, good retail practices 1). An inspection that lost points but has no violation rows in the file is marked
 * `partial`: we never say its violations were none. Every inspection keeps every violation (code, the county's text, points).
 */
function makeLa(fetchedAt: string, hash: string): OfficialParser {
  const src = sourceById("la-county")!;
  const drafts = new Map<string, Draft & { active: boolean; latest: string }>();
  const bySerial = new Map<string, OfficialInspection>();
  const keep = interner();
  const addInspections = (rows: readonly Row[]) => { for (const r of rows) {
    const id = r["record id"] ?? "", date = isoDay(r["activity date"] ?? ""), serial = r["serial number"] ?? "";
    if (!id || !date || !serial) continue;
    const key = `${src.id}:${id}`;
    let d = drafts.get(key);
    if (!d) {
      const name = tidyName(r["program name"] || r["facility name"] || "");
      d = { place: { key, sourceId: src.id, jurisdiction: src.jurisdiction, area: src.area, extId: id, name, address: tidyName(r["facility address"] ?? ""), city: tidyName(r["facility city"] ?? ""), zip: (r["facility zip"] ?? "").slice(0, 5), sourceCategory: keep(r["pe description"] ?? ""), category: laCategory(r["pe description"] ?? "", name), recordUrl: src.lookupUrl, recordHint: src.recordHint, department: src.department }, inspections: new Map(), active: true, latest: "" };
      drafts.set(key, d);
    }
    if (date >= d.latest) { d.latest = date; d.active = !/^inactive$/i.test(r["program status"] ?? ""); }
    if (d.inspections.has(serial)) continue;
    const type = r["service description"] ?? "";
    const i: OfficialInspection = { placeKey: key, date, score: num(r.score), grade: r.grade || null, result: null, scaleId: "CA/los-angeles:score", type: type ? keep(tidyName(type)) : null, routine: /^routine inspection$/i.test(type.trim()), violations: { count: 0, critical: 0, items: [] }, recordUrl: src.lookupUrl };
    d.inspections.set(serial, i); bySerial.set(serial, i);
  } };
  const texts = new Map<string, string>();
  const addViolations = (rows: readonly Row[]) => { for (const r of rows) {
    const serial = r["serial number"] ?? "", i = bySerial.get(serial);
    if (!i || /^in compliance/i.test(r["violation status"] ?? "")) continue;
    const code = (r["violation code"] ?? "").trim(); if (!code) continue;
    const points = num(r.points), critical = points !== null && points >= 2;
    i.violations.count++; if (critical) i.violations.critical++;
    const raw = r["violation description"] ?? "";
    let text = texts.get(raw);
    if (text === undefined) { text = keep(tidyName(raw.replace(/^#\s*\d+[A-Z]?\.\s*/, "")).slice(0, VIOLATION_TEXT_MAX)); texts.set(keep(raw), text); }
    i.violations.items.push({ code: keep(code), text, critical, ...(points !== null ? { points } : {}) });
  } };
  const addInventory = (rows: readonly Row[]) => { for (const r of rows) {
    const d = drafts.get(`${src.id}:${r["record id"] ?? ""}`); if (!d) continue;
    const g = geoOf(r["facility latitude"], r["facility longitude"]);
    if (g.geo) { d.place.geo = g.geo; d.place.geoSource = "source"; }
  } };
  const add = (rows: readonly Row[], role = "inspections") => (role === "violations" ? addViolations(rows) : role === "inventory" ? addInventory(rows) : addInspections(rows));
  const done = () => {
    for (const [k, d] of drafts) if (!d.active) drafts.delete(k);
    for (const d of drafts.values()) for (const i of d.inspections.values()) if (!i.violations.count && i.score !== null && i.score < 100) i.violations.partial = true;
    bySerial.clear();
    return finish(drafts, src.id, fetchedAt, hash);
  };
  return { add, done };
}

/**
 * A parser is fed one page of rows at a time (a 300,000-row source never sits in memory as objects), then gives the places.
 * `drain` (sources sorted by place): the places finished so far, handed to the store as the run goes; `done` gives the rest.
 */
export interface OfficialParser { add(rows: readonly Row[], role?: string): void; done(): OfficialPlaceRecord[]; drain?(): OfficialPlaceRecord[]; held?(): number }
export const PARSERS: Record<string, (fetchedAt: string, hash: string) => OfficialParser> = { nyc: makeNyc, austin: makeAustin, fl: makeFl, la: makeLa, marin: (f, h) => makeMarin(sourceById("marin")!, f, h), sf: makeSfParser };
/** Every row at once (tests and small sources). */
export const parseAll = (parser: string, rows: readonly Row[], fetchedAt: string, hash: string): OfficialPlaceRecord[] => { const p = PARSERS[parser]!(fetchedAt, hash); p.add(rows); return p.done(); };

// ── Areas: what a diner picks ("Miami-Dade County"), each with its source or its link ─────────────────────────────

export interface OfficialArea {
  /** "<state slug>/<area slug>": the cookie value and the page path /find/<slug>/. Stable. */
  slug: string;
  state: UsState;
  area: string;
  jurisdiction: string;
  sourceId: string | null;
  status: "sync" | "link_only";
  department: string;
  lookupUrl: string;
  /** Why it is link only (shown to staff, never to diners). */
  reason?: string;
}

export const areaSlug = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const cleanDept = (name: string) => name.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();

/**
 * Every area we can point a diner to, A to Z within each state: the registry's areas (Florida's statewide source gives one per
 * county), then every other jurisdiction with an inspection lookup in the packs' directory (link only). Needs the states loaded.
 */
export function officialAreas(): OfficialArea[] {
  const out: OfficialArea[] = [];
  const seen = new Set<string>();
  const add = (a: Omit<OfficialArea, "slug">) => {
    const slug = `${STATE_INDEX[a.state].slug}/${areaSlug(a.area)}`;
    if (seen.has(slug)) return;
    seen.add(slug); out.push({ slug, ...a });
  };
  for (const s0 of OFFICIAL_SOURCES) {
    const s = named(s0);
    if (s.id === "fl-dbpr") {
      for (const c of packFor("FL").counties) add({ state: "FL", area: `${c} County`, jurisdiction: s.jurisdiction, sourceId: s.id, status: s.status, department: s.department, lookupUrl: s.lookupUrl });
      continue;
    }
    add({ state: s.state, area: s.area, jurisdiction: s.jurisdiction, sourceId: s.id, status: s.displayApproved === false ? "link_only" : s.status, department: s.department, lookupUrl: s.lookupUrl, ...(s.basis.kind === "link_only" ? { reason: s.basis.reason } : s.displayApproved === false ? { reason: "Collected, waiting for the owner's review before diners see it." } : {}) });
  }
  const covered = new Set(OFFICIAL_SOURCES.map((s) => s.jurisdiction));
  const nodes = new Map(rulesWorld().nodes.map((n) => [n.id, n]));
  for (const e of directoryData().entries) {
    if (e.purpose !== "lookup_inspections" || !e.url || covered.has(e.node)) continue;
    const n = nodes.get(e.node);
    if (!n || n.level === "state" || n.level === "country") continue;
    const st = n.covers.state ?? (e.node.split("/")[0] as UsState);
    const counties = n.covers.counties ?? [];
    // A city department ("Houston (city)") is its city, never "Houston County": Houston County is a different Texas county, and the
    // City of Dallas named "Dallas County" hid Dallas County's own department (same slug, first one kept).
    const one = counties.length === 1 ? counties[0]! : null;
    const area = one ? (st === "MA" ? one : / \(city\)$/.test(one) ? one.replace(/ \(city\)$/, "") : `${one.replace(/ \(.*\)$/, "")} County`) : cleanDept(n.name);
    add({ state: st, area, jurisdiction: e.node, sourceId: null, status: "link_only", department: cleanDept(n.name), lookupUrl: e.url, reason: "No data feed added for this department yet." });
  }
  const order = (a: OfficialArea) => `${STATE_INDEX[a.state].name}|${a.area}`;
  return out.sort((a, b) => (order(a) < order(b) ? -1 : order(a) > order(b) ? 1 : 0));
}
/** A registry entry with its names filled from the pack's node when it leaves them empty. */
export function named(s: OfficialSource): OfficialSource {
  if (s.area && s.department) return s;
  const n = rulesWorld().nodes.find((x) => x.id === s.jurisdiction);
  if (!n) return s;
  const c = n.covers.counties ?? [];
  const department = s.department || cleanDept(n.name);
  return { ...s, department, attribution: s.attribution || department, area: s.area || (c.length === 1 ? (/ \(city\)$/.test(c[0]!) ? c[0]!.replace(/ \(city\)$/, "") : `${c[0]!.replace(/ \(.*\)$/, "")} County`) : cleanDept(n.name)) };
}
export const areaBySlug = (slug: string) => officialAreas().find((a) => a.slug === slug);

/**
 * The area whose department inspects at a point, from the US Census Bureau's names for it ("Use my location" on /find/; the API's
 * GET /geo/locate asks the Census geocoder and keeps nothing). `county` and `place` are the Census BASENAMEs ("Salt Lake",
 * "Pasadena"). A city's own department wins over its county's (Pasadena inside Los Angeles County; Houston inside Harris County),
 * then the county's (one department may cover several counties). Florida's one source has an area per county. Undefined when no
 * area covers the point: the page then offers the state's areas.
 */
export function areaAt(state: string, county: string | null | undefined, place: string | null | undefined): OfficialArea | undefined {
  if (!isSupportedState(state)) return undefined;
  const norm = (s: string) => areaSlug(s.replace(/\s*\([^)]*\)\s*$/, ""));
  const c = county ? norm(county.replace(/\s+(County|Parish|Borough)$/i, "")) : "";
  const p = place ? norm(place) : "";
  const areas = officialAreas().filter((a) => a.state === state);
  const nodes = new Map(rulesWorld().nodes.map((n) => [n.id, n]));
  const covers = (a: OfficialArea) => nodes.get(a.jurisdiction)?.covers.counties ?? [];
  if (p) {
    // A city department: "Pasadena (city)" in the packs; Massachusetts lists its town boards by the town's plain name.
    const city = areas.find((a) => covers(a).some((x) => (/ \(city\)$/.test(x) || state === "MA") && norm(x) === p));
    if (city) return city;
  }
  if (!c) return undefined;
  if (state === "FL") return areas.find((a) => a.slug === `${STATE_INDEX.FL.slug}/${c}-county`);
  const all = areas.filter((a) => covers(a).some((x) => !/ \(city\)$/.test(x) && norm(x) === c));
  return all.find((a) => a.status === "sync") ?? all[0];
}
/** The area a place belongs to (its jurisdiction and area name). */
export const areaFor = (jurisdiction: string, area: string) => officialAreas().find((a) => a.jurisdiction === jurisdiction && a.area === area);

/**
 * How a jurisdiction scores, in plain words, from the state pack's `inspection.scoring` shape (the authority's layer, else the
 * state's), with its citation. Null when the pack has no verified scale. Used on area pages for link-only places too.
 */
export function packScaleLine(jurisdiction: string): { line: string; citation: string; url: string } | null {
  const own = OFFICIAL_SOURCES.find((x) => x.jurisdiction === jurisdiction)?.scaleLine;
  if (own) return own;
  const [st, auth] = jurisdiction.split("/") as [UsState, string | undefined];
  const shapes = SHAPES[st];
  if (!shapes) return null;
  const sc = (auth ? shapes.authorities[auth]?.scoring : undefined) ?? shapes.state.scoring;
  const s = valueOf(sc);
  if (!s || sc?.status !== "verified") return null;
  const bands = (b: { grade: string; min: number; max: number }[]) => b.map((x) => (x.max >= 999 ? `${x.grade} ${x.min} or more` : `${x.grade} ${x.min} to ${x.max}`)).join(", ");
  const line = s.kind === "none" ? "No public score or grade: each inspection lists what was found."
    : s.kind === "score_bands" ? `A score out of ${s.startAt}, higher is better: ${bands(s.bands)}.`
    : s.kind === "demerit_bands" ? `Points added for each problem found, lower is better: ${bands(s.bands)}${s.closedAbove !== undefined ? `; over ${s.closedAbove} closes the place` : ""}.`
    : s.kind === "points_accumulated" ? `Each violation adds ${s.values.join(", ").replace(/, (\d+)$/, " or $1")} points. Lower is better. No letter grade.`
    : `Results are ${s.tiers.map((t) => t.status === "conditional" ? "Conditional Pass" : t.status[0]!.toUpperCase() + t.status.slice(1)).join(", ")}, by the number of critical violations.`;
  return { line, citation: sc.source.citation, url: sc.source.url };
}
